import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import { isRegistrationRoute, trackPageView } from '@/services/AnalyticsService';
import { createEnsureRegistrationHasEmailGuard, createEnsureRegistrationTypeGuard, handleAuthNavigation, isAuthenticated } from './guards';

Vue.use(VueRouter);

const HomeView = () => import('../views/HomeView.vue');
const RegisterPlumberView = () => import('../views/RegisterPlumberView.vue');
const RegisterAssociateView = () => import('../views/RegisterAssociateView.vue');
const RegisterRetiredView = () => import('../views/RegisterRetiredView.vue');
const RegisterTeacherView = () => import('../views/RegisterTeacherView.vue');
const PlumberCompanyDetailsForm = () => import('../components/form/PlumberCompanyDetailsForm.vue');
const PlumberVotingMemberForm = () => import('../components/form/PlumberVotingMemberForm.vue');
const LicensesInsuranceForm = () => import('../components/form/LicensesInsuranceForm.vue');
const PlumberProfileQuestionsForm = () => import('../components/form/PlumberProfileQuestionsForm.vue');
const PaymentForm = () => import('../components/form/PaymentForm.vue');
const ConfirmationForm = () => import('../components/form/ConfirmationForm.vue');
const AssociateCompanyDetailsForm = () => import('../components/form/AssociateCompanyDetailsForm.vue');
const AssociateProfileQuestionsForm = () => import('../components/form/AssociateProfileQuestionsForm.vue');
const RetiredPersonalDetailsForm = () => import('../components/form/RetiredPersonalDetailsForm.vue');
const RetiredVotingMemberForm = () => import('../components/form/RetiredVotingMemberForm.vue');
const RetiredProfileQuestionsForm = () => import('../components/form/RetiredProfileQuestionsForm.vue');
const TeacherPersonalDetailsForm = () => import('../components/form/TeacherPersonalDetailsForm.vue');
const TeacherProfileQuestionsForm = () => import('../components/form/TeacherProfileQuestionsForm.vue');
const SaveView = () => import('../views/SaveView.vue');
const RetrieveView = () => import('../views/RetrieveView.vue');
const LoginView = () => import('../views/LoginView.vue');
const LogoutView = () => import('../views/LogoutView.vue');
const VotingListView = () => import('../views/authenticated/VotingListView.vue');
const VotingDetailView = () => import('../views/authenticated/VotingDetailView.vue');
const ChangeRequestsListView = () => import('../views/authenticated/ChangeRequestsListView.vue');
const ChangeRequestDetailView = () => import('../views/authenticated/ChangeRequestDetailView.vue');

const ensurePlumberRegistration = createEnsureRegistrationTypeGuard(store, 'plumber');
const ensureAssociateRegistration = createEnsureRegistrationTypeGuard(store, 'associate');
const ensureRetiredRegistration = createEnsureRegistrationTypeGuard(store, 'retired');
const ensureTeacherRegistration = createEnsureRegistrationTypeGuard(store, 'teacher');
const ensureRegistrationHasEmail = createEnsureRegistrationHasEmailGuard(store);

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
  },
  {
    path: '/register/plumber',
    redirect: '/register/plumber/1',
    component: RegisterPlumberView,
    props: { firstPath: '/register/plumber/1' },
    children: [
      {
        path: '/register/plumber/1',
        component: PlumberCompanyDetailsForm,
        beforeEnter: ensurePlumberRegistration,
      },
      {
        path: '/register/plumber/2',
        component: PlumberVotingMemberForm,
      },
      {
        path: '/register/plumber/3',
        component: LicensesInsuranceForm,
      },
      {
        path: '/register/plumber/4',
        component: PlumberProfileQuestionsForm,
      },
      {
        path: '/register/plumber/payment',
        component: PaymentForm,
      },
      {
        path: '/register/plumber/completed',
        component: ConfirmationForm,
      },
    ],
  },
  {
    path: '/register/associate',
    redirect: '/register/associate/1',
    component: RegisterAssociateView,
    props: { firstPath: '/register/associate/1' },
    children: [
      {
        path: '/register/associate/1',
        component: AssociateCompanyDetailsForm,
        beforeEnter: ensureAssociateRegistration,
      },
      {
        path: '/register/associate/2',
        component: AssociateProfileQuestionsForm,
      },
      {
        path: '/register/associate/payment',
        component: PaymentForm,
      },
      {
        path: '/register/associate/completed',
        component: ConfirmationForm,
      },
    ],
  },
  {
    path: '/register/retired',
    component: RegisterRetiredView,
    redirect: '/register/retired/1',
    children: [
      {
        path: '/register/retired/1',
        component: RetiredPersonalDetailsForm,
        beforeEnter: ensureRetiredRegistration,
      },
      {
        path: '/register/retired/2',
        component: RetiredVotingMemberForm,
      },
      {
        path: '/register/retired/3',
        component: RetiredProfileQuestionsForm,
      },
      {
        path: '/register/retired/payment',
        component: PaymentForm,
      },
      {
        path: '/register/retired/completed',
        component: ConfirmationForm,
      },
    ],
  },
  {
    path: '/register/teacher',
    component: RegisterTeacherView,
    redirect: '/register/teacher/1',
    children: [
      {
        path: '/register/teacher/1',
        component: TeacherPersonalDetailsForm,
        beforeEnter: ensureTeacherRegistration,
      },
      {
        path: '/register/teacher/2',
        component: TeacherProfileQuestionsForm,
      },
      {
        path: '/register/teacher/payment',
        component: PaymentForm,
      },
      {
        path: '/register/teacher/completed',
        component: ConfirmationForm,
      },
    ],
  },
  {
    path: '/save',
    component: SaveView,
    beforeEnter: ensureRegistrationHasEmail,
  },
  {
    path: '/retrieve',
    component: RetrieveView,
  },
  {
    path: '/login',
    component: LoginView,
    meta: { guestOnly: true },
  },
  {
    path: '/logout',
    component: LogoutView,
    beforeEnter: (to, from, next) => {
      if (isAuthenticated(store)) {
        next();
        return;
      }
      next({ path: '/login' });
    },
  },
  {
    path: '/authenticated/voting',
    component: VotingListView,
    meta: { requiresAuth: true },
  },
  {
    path: '/authenticated/voting/:id',
    component: VotingDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/authenticated/change-requests',
    component: ChangeRequestsListView,
    meta: { requiresAuth: true },
  },
  {
    path: '/authenticated/change-requests/:id',
    component: ChangeRequestDetailView,
    meta: { requiresAuth: true },
  },
];

const router = new VueRouter({
  mode: 'hash',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  const redirect = handleAuthNavigation(to, store);
  if (redirect) {
    next(redirect);
    return;
  }
  next();
});

let isInitialNavigation = true;
router.afterEach((to) => {
  if (isInitialNavigation) {
    isInitialNavigation = false;
    return;
  }

  if (isRegistrationRoute(to.path)) {
    trackPageView();
  }
});

export default router;
